<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router';
import { computed, onMounted, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import VerticalSidebarVue from './vertical-sidebar/VerticalSidebar.vue';
import VerticalHeaderVue from './vertical-header/VerticalHeader.vue';
import HorizontalHeader from './horizontal-header/HorizontalHeader.vue';
import HorizontalSidebar from './horizontal-sidebar/HorizontalSidebar.vue';
import Customizer from './customizer/Customizer.vue';
import PreSetModalLauncher from '@/features/startTask/preset_task/shared/preset/components/PreSetModalLauncher.vue';
import WelcomeModal from '@/shared/components/WelcomeModal.vue';
import ChangeProfileDialog from '@/shared/components/changeProfile/ChangeProfileDialog.vue';
import { useNotificationStream } from '@/features/inbox/composables/useNotificationStream';
import { useCustomizerStore } from '@/shared/stores/customizer';
import { useSubscriptionStatus } from '@/shared/composables/useSubscriptionStatus';
import { useTermsConsent } from '@/shared/composables/useTermsConsent';
import TermsConsentDialog from '@/shared/components/legal/TermsConsentDialog.vue';
import config from '@/shared/config/config';

const { t } = useI18n();
const customizer = useCustomizerStore();
const route = useRoute();

useNotificationStream();
// 배너 관련(subscriptionStatus/showBanner/bannerMessage)은 CTA 배너 제거로 미사용 → destructure 제외.
const { accessBlocked, blockedTitle, blockedMessage } = useSubscriptionStatus();

// 약관·개인정보 동의 게이트. 두 모드가 있다.
//  - needsConsent        : 강제 기준 미동의 → 닫기 불가 하드 차단 모달(기존 동작)
//  - showUpcomingNotice  : 시행 전 최신본 미동의 → 닫을 수 있는 안내 모달(차단 아님)
// 둘 다 같은 컴포넌트를 쓰고 notice prop 으로 갈라진다.
const { needsConsent, showUpcomingNotice } = useTermsConsent();
const consentDialogOpen = ref(false);
watch(
  [needsConsent, showUpcomingNotice],
  ([blocked, notice]) => { consentDialogOpen.value = !!blocked || !!notice; },
  { immediate: true },
);
const { width } = useDisplay();
const router = useRouter();

// 하드 차단 팝업: 결제 필요 시 안내. 닫아도 라우터 가드가 결제화면 외 접근을 제한.
// 이미 결제 화면에 있으면 띄우지 않는다 — "결제 페이지로 가세요" 팝업이 이미 그 페이지 위에
// 뜨면서 뒤에 있는 Renew/Change 버튼을 가로막는 건 누가 봐도 이상한 UX다.
const blockedDialogOpen = ref(false);
// 결제 화면은 /settings/billing 로 이관됨. 구 /pages/company-settings 도 인식(리다이렉트 경유 호환).
const isOnBillingPage = computed(() => route.path.startsWith('/settings/billing') || route.path.startsWith('/pages/company-settings'));
watch(accessBlocked, (v) => {
  // 로그인 직후 라우터 가드가 차단을 감지하면 blocked=1을 달고 곧장 결제화면으로 리다이렉트한다.
  // 그 시점엔 이미 isOnBillingPage=true라 "이미 결제화면에 있으니 팝업 생략" 로직에 걸려 팝업이
  // 영원히 안 뜬다 — 이 경우는 "결제화면을 보다가 만료됨"이 아니라 "로그인하자마자 차단돼서
  // 결제화면으로 밀려난" 것이므로 구분해서 최초 1회는 띄워야 한다.
  const justRedirectedForBlock = route.query.blocked === '1';
  if (v && (!isOnBillingPage.value || justRedirectedForBlock)) {
    blockedDialogOpen.value = true;
    if (justRedirectedForBlock) {
      const { blocked, ...rest } = route.query;
      router.replace({ path: route.path, query: rest });
    }
  }
}, { immediate: true });
// 팝업이 떠있는 상태에서 사용자가 결제 화면으로 이동하면(배너의 "Go To Billing" 등) 자동으로 닫는다.
watch(isOnBillingPage, (onBilling) => { if (onBilling) blockedDialogOpen.value = false; });
function goToBilling() {
  blockedDialogOpen.value = false;
  router.push('/settings/billing');
}

// mini-sidebar 스타일이 적용되는 최소 너비 (scss의 @media min-width: 1170px와 일치)
const MINI_SIDEBAR_BREAKPOINT = 1170;

// 화면 크기가 작아지면 mini_sidebar 상태에서 사이드바를 숨김
watch(width, (newWidth) => {
  if (newWidth < MINI_SIDEBAR_BREAKPOINT && customizer.mini_sidebar && customizer.Sidebar_drawer) {
    // mini-sidebar 상태에서 화면이 작아지면 사이드바를 숨김
    customizer.SET_SIDEBAR_VISIBLE(false);
  }
}, { immediate: true });

// _refresh 파라미터가 있으면 컴포넌트를 강제로 재마운트하기 위한 key
const routerViewKey = computed(() => {
  const refresh = route.query._refresh;
  // _refresh가 있으면 key를 변경하여 강제 재마운트
  return refresh ? `${route.path}-${refresh}` : route.path;
});

onMounted(() => {
  console.log('FullLayout - boxed value:', customizer.boxed);
  console.log('FullLayout - customizer state:', customizer.$state);

  // config.ts 직접 확인
  console.log('Config.ts - boxed value:', config.boxed);
});
</script>

<template>
     <!-----RTL LAYOUT------->
     <v-locale-provider v-if="customizer.setRTLLayout" rtl>
        <v-app
            :theme="customizer.actTheme"
            :class="[
                customizer.actTheme,
                customizer.mini_sidebar ? 'mini-sidebar' : '',
                customizer.setHorizontalLayout ? 'horizontalLayout' : 'verticalLayout',
                customizer.setBorderCard ? 'cardBordered' : '',
                customizer.inputBg ? 'inputWithbg' : ''
            ]"
        >
            <!---Customizer location left side--->
            <v-navigation-drawer app temporary elevation="10" location="left" v-model="customizer.Customizer_drawer" width="320" class="left-customizer">
                <Customizer />
            </v-navigation-drawer>
            <VerticalHeaderVue v-if="!customizer.setHorizontalLayout" />
            <VerticalSidebarVue v-if="!customizer.setHorizontalLayout" />
            <HorizontalHeader v-if="customizer.setHorizontalLayout" />
            <HorizontalSidebar v-if="customizer.setHorizontalLayout" />

            <v-main>
                <!-- (2026-09) 구독 상태 배너/‘Go To Billing’ CTA 제거: 하드차단 시 라우터 가드가
                     /settings/billing 로 자동 이동시키므로 인라인 CTA가 중복이었다. -->

                <v-container fluid class="page-wrapper pb-sm-15 pb-10">
                    <div :class="customizer.boxed ? 'maxWidth' : ''" style="width: 100%;">
                        <RouterView :key="routerViewKey" />
                    </div>
                </v-container>
            </v-main>
            <!-- 전역 PreSet 모달 — 어느 메뉴에서든 Start Task 클릭 시 노출 -->
            <PreSetModalLauncher />
            <!-- 최초 방문 Welcome 모달 (서버 플래그 welcomeSeen 기준) -->
            <WelcomeModal />
            <ChangeProfileDialog />
            <v-dialog :model-value="blockedDialogOpen" @update:model-value="(v: boolean) => blockedDialogOpen = v" max-width="440" persistent>
              <v-card rounded="lg" class="pa-2">
                <v-card-item>
                  <v-icon color="warning" size="34" class="mb-2">mdi-lock-alert-outline</v-icon>
                  <v-card-title class="text-h6 font-weight-bold" style="white-space:normal">{{ blockedTitle }}</v-card-title>
                </v-card-item>
                <v-card-text class="text-body-2 text-medium-emphasis">{{ blockedMessage }}</v-card-text>
                <v-card-actions class="px-4 pb-4">
                  <v-spacer />
                  <v-btn color="primary" variant="flat" rounded="pill" @click="goToBilling">{{ t('common.subscriptionAccess.goToPayment') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
        </v-app>
    </v-locale-provider>

     <!-----LTR LAYOUT------->
     <v-locale-provider v-else>
        <v-app
            :theme="customizer.actTheme"
            :class="[
                customizer.actTheme,
                customizer.mini_sidebar ? 'mini-sidebar' : '',
                customizer.setHorizontalLayout ? 'horizontalLayout' : 'verticalLayout',
                customizer.setBorderCard ? 'cardBordered' : '',
                customizer.inputBg ? 'inputWithbg' : ''
            ]"
        >
            <!---Customizer location right side--->
            <v-navigation-drawer app temporary elevation="10" location="right" v-model="customizer.Customizer_drawer" width="320">
                <Customizer />
            </v-navigation-drawer>
            <VerticalHeaderVue v-if="!customizer.setHorizontalLayout" />
            <VerticalSidebarVue v-if="!customizer.setHorizontalLayout" />
            <HorizontalHeader v-if="customizer.setHorizontalLayout" />
            <HorizontalSidebar v-if="customizer.setHorizontalLayout" />
            <v-main>
                <!-- (2026-09) 구독 상태 배너/‘Go To Billing’ CTA 제거: 하드차단 시 라우터 가드가
                     /settings/billing 로 자동 이동시키므로 인라인 CTA가 중복이었다. -->

                <v-container fluid class="page-wrapper pb-sm-15 pb-10">
                    <div :class="customizer.boxed ? 'maxWidth' : ''" style="width: 100%;">
                        <RouterView :key="routerViewKey" />
                    </div>
                </v-container>
            </v-main>
            <!-- 전역 PreSet 모달 — 어느 메뉴에서든 Start Task 클릭 시 노출 -->
            <PreSetModalLauncher />
            <!-- 최초 방문 Welcome 모달 (서버 플래그 welcomeSeen 기준) -->
            <WelcomeModal />
            <ChangeProfileDialog />
            <v-dialog :model-value="blockedDialogOpen" @update:model-value="(v: boolean) => blockedDialogOpen = v" max-width="440" persistent>
              <v-card rounded="lg" class="pa-2">
                <v-card-item>
                  <v-icon color="warning" size="34" class="mb-2">mdi-lock-alert-outline</v-icon>
                  <v-card-title class="text-h6 font-weight-bold" style="white-space:normal">{{ blockedTitle }}</v-card-title>
                </v-card-item>
                <v-card-text class="text-body-2 text-medium-emphasis">{{ blockedMessage }}</v-card-text>
                <v-card-actions class="px-4 pb-4">
                  <v-spacer />
                  <v-btn color="primary" variant="flat" rounded="pill" @click="goToBilling">{{ t('common.subscriptionAccess.goToPayment') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 약관·개인정보 동의 게이트 (무조건 동의, 미동의 시 서비스 이용 불가) -->
            <TermsConsentDialog v-model="consentDialogOpen" :notice="!needsConsent && showUpcomingNotice" />
        </v-app>
    </v-locale-provider>
</template>
